import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable, type DataTableSummary } from '@aeg-presents/components/DataTable';
import { TicketAllocation, TicketShow } from '@app-types/ticketSales';
import { allocationTableColumns } from './AllocationTable.columns';
import {
  isManualSalesEditable,
  sortAllocations,
  sumAllocationTotals,
  withRecalculatedSales,
} from './AllocationTable.utils';

export type AllocationTableProps = {
  show: TicketShow;
  visible?: boolean;
};

function toRows(allocations: TicketShow['allocations']): TicketAllocation[] {
  return sortAllocations(allocations).map((allocation) => ({ ...allocation }));
}

export function AllocationTable({ show, visible = true }: AllocationTableProps) {
  const [rows, setRows] = useState<TicketAllocation[]>(() => toRows(show.allocations));

  useEffect(() => {
    setRows(toRows(show.allocations));
  }, [show.allocations]);

  const totals = useMemo(() => sumAllocationTotals(rows), [rows]);

  const summary = useMemo<DataTableSummary>(
    () => ({
      visible: true,
      id: `${show.id}-show-total`,
      label: 'Show Total',
      labelField: 'ticketCompany',
      cells: [
        { field: 'ticketType', visible: false },
        { field: 'ticketName', visible: false },
        { field: 'allocation', value: totals.allocation },
        { field: 'price', visible: false },
        { field: 'lastUpdated', visible: false },
        { field: 'feedSales', value: totals.feedSales },
        { field: 'manualSales', value: totals.manualSales },
        { field: 'totalSales', value: totals.totalSales },
        { field: 'percentSold', visible: false },
        { field: 'unsold', value: totals.unsold },
      ],
    }),
    [show.id, totals],
  );

  const processRowUpdate = useCallback((newRow: TicketAllocation) => {
    const parsedManualSales = Number(newRow.manualSales);
    const manualSales = Number.isFinite(parsedManualSales) ? parsedManualSales : 0;
    const updatedRow = withRecalculatedSales(newRow, manualSales);

    setRows((current) => current.map((row) => (row.id === updatedRow.id ? updatedRow : row)));

    return updatedRow;
  }, []);

  return (
    <DataTable
      visible={visible}
      rows={rows}
      columns={allocationTableColumns}
      summary={summary}
      isCellEditable={(row, field) => field === 'manualSales' && isManualSalesEditable(row)}
      processRowUpdate={processRowUpdate}
      dataTestID={`allocation-table-${show.id}`}
    />
  );
}
